import React from 'react';
import { useTranslation } from 'react-i18next';
import { useJobHealth, useActiveJobsCount } from '@/hooks/useJobHealth';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertCircle, CheckCircle, AlertTriangle, HelpCircle } from 'lucide-react';
import { deriveJobHealthView } from '@/lib/jobHealthView';
import { Spinner } from '@/components/ui/spinner';

/**
 * P2 FIX: Job Health Indicator Component
 * Shows real-time status of optimization jobs in the sidebar or header
 */
export function JobHealthIndicator() {
  const { t } = useTranslation('common');
  const { data: health, isLoading: healthLoading, isError: healthError } = useJobHealth();
  const { data: activeJobs, isLoading: jobsLoading } = useActiveJobsCount();
  const view = deriveJobHealthView({ healthLoading, jobsLoading, healthError, health, activeJobs });

  if (view === 'loading') {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <Spinner size="sm" />
        <span>{t('jobHealth.checking')}</span>
      </div>
    );
  }

  const issueCount = health?.health?.issues?.length ?? 0;
  // Erkannte Probleme bleiben sichtbar, auch wenn Jobs laufen: ein haengender Job
  // steht selbst auf 'processing' und zaehlt als aktiv. `view` bestimmt nur den Text.
  const hasIssues = health?.health ? !health.health.healthy : false;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors cursor-default">
            {hasIssues ? (
              <AlertCircle className="h-4 w-4 text-destructive" />
            ) : view === 'running' ? (
              <Spinner size="sm" />
            ) : view === 'unknown' ? (
              <HelpCircle className="h-4 w-4 text-muted-foreground" />
            ) : (
              <CheckCircle className="h-4 w-4 text-success" />
            )}

            <span className="text-sm">
              {view === 'running' ? (
                <span className="text-primary font-medium">{t('jobHealth.jobsRunning', { count: activeJobs })}</span>
              ) : view === 'issues' ? (
                <span className="text-destructive">{t('jobHealth.issuesDetected')}</span>
              ) : view === 'unknown' ? (
                <span className="text-muted-foreground">{t('jobHealth.unknown')}</span>
              ) : (
                <span className="text-muted-foreground">{t('jobHealth.allHealthy')}</span>
              )}
            </span>

            {hasIssues && issueCount > 0 && (
              <Badge variant="destructive" className="ml-auto text-xs">
                {issueCount}
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          <div className="space-y-2">
            <p className="font-medium">{t('jobHealth.jobStatus')}</p>
            <div className="text-sm space-y-1">
              <p>{t('jobHealth.activeJobs')} {activeJobs || 0}</p>
              {health?.health ? (
                <>
                  <p>{t('jobHealth.recentFailures')} {health.health.recentFailures}</p>
                  <p>{t('jobHealth.staleJobs')} {health.health.staleJobs}</p>
                </>
              ) : (
                <p>{t('jobHealth.unknownHint')}</p>
              )}
            </div>
            {hasIssues && issueCount > 0 && (
              <div className="border-t pt-2 mt-2">
                <p className="font-medium text-destructive mb-1">{t('jobHealth.issues')}</p>
                <ul className="text-xs space-y-1">
                  {health?.health.issues.map((issue, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <AlertTriangle className="h-3 w-3 mt-0.5 text-destructive shrink-0" />
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Compact version for header/navbar
 */
export function JobHealthBadge() {
  const { t } = useTranslation('common');
  const { data: activeJobs } = useActiveJobsCount();
  const { data: health } = useJobHealth();

  const hasIssues = health && !health.health.healthy;
  const hasActiveJobs = activeJobs && activeJobs > 0;

  if (!hasActiveJobs && !hasIssues) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant={hasIssues ? "destructive" : "secondary"}
            className="gap-1 cursor-default"
          >
            {hasActiveJobs && (
              <>
                <Spinner size="sm" />
                {activeJobs}
              </>
            )}
            {hasIssues && !hasActiveJobs && (
              <>
                <AlertCircle className="h-3 w-3" />
                {t('jobHealth.issuesDetected')}
              </>
            )}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          {hasActiveJobs && <p>{t('jobHealth.optimizationInProgress', { count: activeJobs })}</p>}
          {hasIssues && <p>{t('jobHealth.jobIssues')}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}